import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormFieldError,
  AdminFormDropdownChevron,
  AdminSupportingText,
  AdminAlertText,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminReadOnlyField,
  AdminCompactFormPage,
  ADMIN_COMPACT_FORM_SHELL_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  BUSINESS_ENTRY_POINT_OPTIONS,
  COMPLIANCE_INFORMATION_REQUESTS_NOTE,
  CUSTOMER_ENTRY_POINT_OPTIONS,
  MESSAGES,
  PRIORITY_OPTIONS,
  SUPPORT_TICKET_CONFIG_ROUTE,
  TICKET_TYPES,
  VISITOR_ENTRY_POINT,
  buildSampleSupportTicketConfigurations,
  findSupportTicketConfiguration,
  formatSupportTicketConfigDate,
  getAvailabilityLabel,
  getAvailableTicketTypesForAdd,
  getConditionsSummary,
  normalizeSupportTicketConfiguration,
} from '../../../data/supportTicketConfig';
import {
  buildSupportTicketConfigActor,
  canAddSupportTicketConfiguration,
  canEditSupportTicketConfiguration,
  canSaveSupportTicketConfiguration,
  canViewSupportTicketConfiguration,
  saveSupportTicketConfiguration,
} from '../../../data/supportTicketConfigWorkflow';

const RequiredMark = () => <span className="text-alertred">*</span>;

const FieldError = ({ error }) =>
  error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null;

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">
        {value || '—'}
      </AdminDetailFieldValue>
    )}
  </div>
);

const AvailabilityToggle = ({ label, checked, onChange, error }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} <RequiredMark />
    </AdminDetailFieldLabel>
    <label className="relative inline-flex items-center cursor-pointer py-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="relative w-9 h-5 bg-lightgray rounded-full peer-checked:bg-primaryblue after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
      <span className="ml-2 text-sm text-offblack">{getAvailabilityLabel(checked)}</span>
    </label>
    <FieldError error={error} />
  </div>
);

const EntryPointCheckboxes = ({ label, options, values, onToggle, error }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} <RequiredMark />
    </AdminDetailFieldLabel>
    <div className="space-y-2 py-1">
      {options.map((option) => {
        const checked = (values || []).includes(option.value);
        return (
          <label key={option.value} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-lightgray"
              checked={checked}
              onChange={() => onToggle(option.value)}
            />
            <span className="text-sm text-offblack">{option.label}</span>
          </label>
        );
      })}
    </div>
    <FieldError error={error} />
  </div>
);

function formatEntryPoints(values) {
  if (!Array.isArray(values) || values.length === 0) return '—';
  return values.join('; ');
}

function emptyDraft() {
  return normalizeSupportTicketConfiguration({
    id: '',
    ruleName: '',
    description: '',
    ticketType: '',
    visitorAvailable: false,
    customerAvailable: false,
    businessAvailable: false,
    customerEntryPoints: [],
    businessEntryPoints: [],
    automaticPriority: '',
    changeReason: '',
  });
}

const SupportTicketConfigurationDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { configId } = useParams();
  const { auth } = useAuth();
  const actor = useMemo(() => buildSupportTicketConfigActor(auth), [auth]);

  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isCreate && !isEdit;
  const canAdd = canAddSupportTicketConfiguration(auth);
  const canEdit = canEditSupportTicketConfiguration(auth);
  const canSave = canSaveSupportTicketConfiguration(auth);
  const canView = canViewSupportTicketConfiguration(auth);

  const [configurations] = useState(
    () => location.state?.configurations || buildSampleSupportTicketConfigurations(),
  );

  const baseline = useMemo(() => {
    if (isCreate) return null;
    const fromState = location.state?.configuration
      ? normalizeSupportTicketConfiguration(location.state.configuration)
      : null;
    const fromList = findSupportTicketConfiguration(configurations, configId);
    return fromState?.id === decodeURIComponent(String(configId || ''))
      ? fromState
      : fromList
        ? normalizeSupportTicketConfiguration(fromList)
        : null;
  }, [isCreate, location.state?.configuration, configurations, configId]);

  const [draft, setDraft] = useState(() => (isCreate ? emptyDraft() : baseline));
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [openedLastUpdated] = useState(() => baseline?.lastUpdated || null);

  useEffect(() => {
    if (isCreate) {
      setDraft(emptyDraft());
    } else if (baseline) {
      setDraft(normalizeSupportTicketConfiguration(baseline));
    }
    setFieldErrors({});
    setFormError('');
  }, [isCreate, baseline]);

  const availableTicketTypes = useMemo(
    () => getAvailableTicketTypesForAdd(configurations),
    [configurations],
  );

  const updateDraft = (patch) => {
    setDraft((prev) => normalizeSupportTicketConfiguration({ ...prev, ...patch }));
  };

  const toggleAvailability = (field, checked) => {
    const entryPointsField =
      field === 'customerAvailable' ? 'customerEntryPoints' : 'businessEntryPoints';
    setDraft((prev) =>
      normalizeSupportTicketConfiguration({
        ...prev,
        [field]: checked,
        ...(field === 'visitorAvailable' ? {} : { [entryPointsField]: [] }),
      }),
    );
  };

  const toggleCustomerEntryPoint = (value) => {
    if (!draft?.customerAvailable) return;
    setDraft((prev) => {
      const current = prev?.customerEntryPoints || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return normalizeSupportTicketConfiguration({ ...prev, customerEntryPoints: next });
    });
  };

  const toggleBusinessEntryPoint = (value) => {
    if (!draft?.businessAvailable) return;
    setDraft((prev) => {
      const current = prev?.businessEntryPoints || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return normalizeSupportTicketConfiguration({ ...prev, businessEntryPoints: next });
    });
  };

  const handleSave = () => {
    if (!draft) {
      setFormError(MESSAGES.notFound);
      return;
    }
    if (isCreate && !canAdd) {
      setFieldErrors({ form: 'You do not have permission to save this Rule.' });
      setFormError('You do not have permission to save this Rule.');
      return;
    }
    if (!isCreate && !canEdit) {
      setFieldErrors({ form: 'You do not have permission to save this Rule.' });
      setFormError('You do not have permission to save this Rule.');
      return;
    }

    const result = saveSupportTicketConfiguration(configurations, draft, actor, {
      isAdd: isCreate,
      baseline,
      openedLastUpdated,
      canSave,
    });

    if (!result.success) {
      setFieldErrors(result.errors || {});
      const messages = [...new Set(Object.values(result.errors || {}).filter(Boolean))];
      setFormError(messages.join(' ') || result.message);
      return;
    }

    setFieldErrors({});
    setFormError('');
    navigate(SUPPORT_TICKET_CONFIG_ROUTE, {
      state: {
        configurations: result.configurations,
        feedback: result.noChange ? undefined : result.message,
      },
    });
  };

  if (!canView && isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Support Ticket Rule Detail</AdminPageTitle>
        <AdminFormFieldError>You do not have permission to view this Rule.</AdminFormFieldError>
      </AdminLayout>
    );
  }

  if (!draft) {
    return (
      <AdminLayout>
        <div className={ADMIN_COMPACT_FORM_SHELL_CLASS}>
          <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>
            {isCreate
              ? 'Add Support Ticket Rule'
              : isView
                ? 'Support Ticket Rule Detail'
                : 'Edit Support Ticket Rule'}
          </AdminPageTitle>
          <AdminSupportingText className="text-mediumgray">{MESSAGES.notFound}</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (isView) {
    const isGeneralEnquiry = draft.ticketType === TICKET_TYPES.GENERAL_ENQUIRY;
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Support Ticket Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={draft.id} mono />
              <TextValue label="Rule Name" value={draft.ruleName} />
              <div className="sm:col-span-2">
                <TextValue label="Description" value={draft.description} />
              </div>
              <TextValue label="Ticket Type" value={draft.ticketType} />
              <TextValue
                label="Visitor Availability"
                value={getAvailabilityLabel(Boolean(draft.visitorAvailable))}
              />
              {draft.visitorAvailable ? (
                <TextValue label="Visitor Entry Point" value={VISITOR_ENTRY_POINT} />
              ) : null}
              <TextValue
                label="Customer Availability"
                value={getAvailabilityLabel(Boolean(draft.customerAvailable))}
              />
              {draft.customerAvailable ? (
                <TextValue
                  label="Customer Entry Points"
                  value={formatEntryPoints(draft.customerEntryPoints)}
                />
              ) : null}
              <TextValue
                label="Business Availability"
                value={getAvailabilityLabel(Boolean(draft.businessAvailable))}
              />
              {draft.businessAvailable ? (
                <TextValue
                  label="Business Entry Points"
                  value={formatEntryPoints(draft.businessEntryPoints)}
                />
              ) : null}
              <TextValue label="Automatic Priority" value={draft.automaticPriority} />
              {isGeneralEnquiry ? (
                <div className="sm:col-span-2">
                  <TextValue
                    label="Compliance Information Requests"
                    value={COMPLIANCE_INFORMATION_REQUESTS_NOTE}
                  />
                </div>
              ) : null}
              <div className="sm:col-span-2">
                <TextValue label="Conditions" value={getConditionsSummary(draft)} />
              </div>
              <TextValue
                label="Date Added"
                value={formatSupportTicketConfigDate(draft.dateAdded)}
              />
              <TextValue label="Added By" value={draft.addedBy} />
              <TextValue
                label="Last Updated"
                value={formatSupportTicketConfigDate(draft.lastUpdated)}
              />
              <TextValue label="Last Updated By" value={draft.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  const isGeneralEnquiry = draft.ticketType === TICKET_TYPES.GENERAL_ENQUIRY;
  const validationErrorList = [...new Set(Object.values(fieldErrors).filter(Boolean))];
  const formTitle = isCreate ? 'Add Support Ticket Rule' : 'Edit Support Ticket Rule';

  return (
    <AdminLayout>
      <AdminCompactFormPage title={formTitle} onSubmit={handleSave} showSave={false}>
          {/* Rule Details */}
          <div className="space-y-4 w-full min-w-0">
            {!isCreate ? (
              <AdminReadOnlyField label="Rule ID" value={draft.id} mono />
            ) : null}
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Rule Name <RequiredMark />
              </AdminDetailFieldLabel>
              <input
                type="text"
                placeholder="Enter a Rule Name"
                value={draft.ruleName || ''}
                onChange={(e) => updateDraft({ ruleName: e.target.value })}
                className={adminFormDetailInputClasses(false, Boolean(fieldErrors.ruleName))}
              />
              <FieldError error={fieldErrors.ruleName} />
            </div>
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Description <RequiredMark />
              </AdminDetailFieldLabel>
              <textarea
                placeholder="Enter a Description"
                value={draft.description || ''}
                onChange={(e) => updateDraft({ description: e.target.value })}
                className={adminFormDetailTextareaClasses(false, Boolean(fieldErrors.description))}
                rows={3}
              />
              <FieldError error={fieldErrors.description} />
            </div>
          </div>

          {/* Rule Scope */}
          <div className="space-y-4 w-full min-w-0">
            <AdminPanelTitle className="text-offblack !mb-0">Rule Scope</AdminPanelTitle>
            {isCreate ? (
              <div className="space-y-1 w-full min-w-0">
                <AdminDetailFieldLabel>
                  Ticket Type <RequiredMark />
                </AdminDetailFieldLabel>
                <div className="relative w-full min-w-0">
                  <select
                    value={draft.ticketType || ''}
                    onChange={(e) => updateDraft({ ticketType: e.target.value })}
                    disabled={availableTicketTypes.length === 0}
                    className={adminFormDetailSelectClasses(
                      availableTicketTypes.length === 0,
                      Boolean(fieldErrors.ticketType),
                    )}
                  >
                    <option value="">
                      {availableTicketTypes.length === 0
                        ? 'No compatible choices available'
                        : 'Select Ticket Type'}
                    </option>
                    {availableTicketTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <AdminFormDropdownChevron />
                </div>
                <FieldError error={fieldErrors.ticketType} />
              </div>
            ) : (
              <AdminReadOnlyField label="Ticket Type" value={draft.ticketType} />
            )}
          </div>

          {/* Rule Settings */}
          <div className="space-y-4 w-full min-w-0">
            <AdminPanelTitle className="text-offblack !mb-0">Rule Settings</AdminPanelTitle>

            <AvailabilityToggle
              label="Visitor Availability"
              checked={Boolean(draft.visitorAvailable)}
              onChange={(checked) => toggleAvailability('visitorAvailable', checked)}
              error={fieldErrors.visitorAvailable}
            />
            {draft.visitorAvailable ? (
              <AdminReadOnlyField label="Visitor Entry Point" value={VISITOR_ENTRY_POINT} />
            ) : null}

            <AvailabilityToggle
              label="Customer Availability"
              checked={Boolean(draft.customerAvailable)}
              onChange={(checked) => toggleAvailability('customerAvailable', checked)}
              error={fieldErrors.customerAvailable}
            />
            {draft.customerAvailable ? (
              <EntryPointCheckboxes
                label="Customer Entry Points"
                options={CUSTOMER_ENTRY_POINT_OPTIONS}
                values={draft.customerEntryPoints}
                onToggle={toggleCustomerEntryPoint}
                error={fieldErrors.customerEntryPoints}
              />
            ) : null}

            <AvailabilityToggle
              label="Business Availability"
              checked={Boolean(draft.businessAvailable)}
              onChange={(checked) => toggleAvailability('businessAvailable', checked)}
              error={fieldErrors.businessAvailable}
            />
            {draft.businessAvailable ? (
              <EntryPointCheckboxes
                label="Business Entry Points"
                options={BUSINESS_ENTRY_POINT_OPTIONS}
                values={draft.businessEntryPoints}
                onToggle={toggleBusinessEntryPoint}
                error={fieldErrors.businessEntryPoints}
              />
            ) : null}

            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Automatic Priority <RequiredMark />
              </AdminDetailFieldLabel>
              <div className="relative w-full min-w-0">
                <select
                  className={adminFormDetailSelectClasses(false, Boolean(fieldErrors.automaticPriority))}
                  value={draft.automaticPriority || ''}
                  onChange={(e) => updateDraft({ automaticPriority: e.target.value })}
                >
                  <option value="">Select Automatic Priority</option>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <AdminFormDropdownChevron />
              </div>
              <FieldError error={fieldErrors.automaticPriority} />
            </div>

            {isGeneralEnquiry ? (
              <div className="space-y-1 w-full min-w-0">
                <AdminDetailFieldLabel>Compliance Information Requests</AdminDetailFieldLabel>
                <AdminSupportingText className="text-mediumgray mb-0">
                  {COMPLIANCE_INFORMATION_REQUESTS_NOTE}
                </AdminSupportingText>
              </div>
            ) : null}
          </div>

          {/* Optional Conditions */}
          <div className="space-y-3 w-full min-w-0">
            <AdminPanelTitle className="text-offblack !mb-0">Optional Conditions</AdminPanelTitle>
            <AdminSupportingText className="text-offblack mb-0">No Conditions</AdminSupportingText>
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-1.5 text-sm font-medium text-mediumgray cursor-not-allowed"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Condition
            </button>
            <AdminSupportingText className="text-mediumgray mb-0">
              No compatible Conditions available
            </AdminSupportingText>
          </div>

          {/* Validation Messages */}
          <div className="space-y-2 w-full min-w-0">
            <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
            {validationErrorList.length > 0 ? (
              validationErrorList.map((msg) => (
                <AdminAlertText key={msg} className="mb-0">
                  {msg}
                </AdminAlertText>
              ))
            ) : formError ? (
              <AdminAlertText className="mb-0">{formError}</AdminAlertText>
            ) : (
              <AdminSupportingText className="text-mediumgray mb-0">
                No validation messages.
              </AdminSupportingText>
            )}
          </div>

          {!isCreate ? (
            <div className="space-y-1 w-full min-w-0">
              <AdminDetailFieldLabel>
                Change Reason <RequiredMark />
              </AdminDetailFieldLabel>
              <textarea
                placeholder="Enter a Change Reason"
                value={draft.changeReason || ''}
                onChange={(e) => updateDraft({ changeReason: e.target.value })}
                className={adminFormDetailTextareaClasses(false, Boolean(fieldErrors.changeReason))}
                rows={3}
              />
              <FieldError error={fieldErrors.changeReason} />
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3">
            <AdminHeaderAction type="submit" disabled={!canSave}>
              Save
            </AdminHeaderAction>
          </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default SupportTicketConfigurationDetail;
